import { HourlyProject, TimeEntryHourly } from '../interfaces/EmployeeInterface';
import { isServiceOrderRow, serviceOrderMemoText, ServiceOrderRowFields } from './serviceOrderRow';

/**
 * TAS-514 — how an NPM service-order punch reads, shared by every AdminApp surface that
 * shows one: the v2 Timesheet Review console, the payroll-edit hourly modal, Vista
 * Export V2 and the Vista Post Verification report.
 *
 * NPM ONLY. Every caller gates on `config.tehPunchesAreHourly` (or an NPM-only page)
 * before calling in — never this module. On Wallcraft `ServiceOrderText` holds a
 * PURCHASE ORDER number, so reading it as a service order there would invent
 * customer-service work that does not exist; 2020PH and WCDP record the same fact as a
 * linked ServiceOrder and a CustomerServiceWarranty claim respectively, neither of which
 * has a scope, and both already have their own rendering that must not change.
 */

/** One NPM service-order punch, resolved for display. */
export interface NpmServiceOrderInfo {
  /** The number the punch actually carries: the validated Vista number first, then the
   *  legacy free text, then whatever the employee typed and Vista has not seen yet. */
  number: string;
  /** The Vista work-order description; null while the punch has no work order to describe. */
  name: string | null;
  /** `Scope 2 · PLUMBING · Rough-in · Job 29-1140` — empty when the punch has no scope. */
  scope: string;
  /** "Awaiting validation" / "Not validated"; null once the punch is linked to Vista. */
  state: string | null;
  /** True once the punch is linked to a real Vista work order. */
  validated: boolean;
  /** The ServiceOrderScope FK, so an editor can round-trip it without re-deriving it. */
  serviceOrderScopeID: number | null;
}

/**
 * Reads the NPM service order off an hourly punch, in whichever of its three shapes it
 * arrived in:
 *
 *   legacy     ServiceOrderText only, never matched against Vista           → "Not validated"
 *   temporary  TempServiceOrderDescription, no ids — the employee typed a   → "Awaiting validation"
 *              number Vista does not have yet (residential service orders
 *              reach Vista a median 11 days after the first punch)
 *   validated  ServiceOrderID + ServiceOrderScopeID, resolved on mobile     → the scope line
 *
 * Returns null when the punch carries no service order at all.
 *
 * Entry-level first with the hourly group as the fallback, matching how `serviceOrderText`
 * has always been read: the API flattens the scope onto BOTH the punch
 * (`TimeEntryHourly.serviceOrderScope`) and its group (`HourlyProjectGroup.scope*`), and
 * which one is populated depends on the read the caller used.
 */
export function describeNpmServiceOrder(
  hp: Pick<HourlyProject, 'serviceOrderText' | 'serviceOrderAltID' | 'serviceOrderID'
    | 'serviceOrderScopeID' | 'scopeSeq' | 'scopeDescription' | 'scopeDivision' | 'scopeJob'
    | 'tempServiceOrderDescription'> | null | undefined,
  entry: Pick<TimeEntryHourly, 'serviceOrderText' | 'serviceOrder' | 'serviceOrderID'
    | 'serviceOrderScope' | 'serviceOrderScopeID' | 'tempServiceOrderDescription'> | null | undefined,
): NpmServiceOrderInfo | null {
  // TAS-517 — one flat row, so the SAME predicate the Vista exporter runs decides whether
  // this punch is service-order work. `serviceOrderText` is carried RAW: `isServiceOrderRow`
  // pins the legacy test at `length > 0` untrimmed to match the API byte for byte, which
  // means a whitespace-only free text is a service-order punch here too. It is, in Vista —
  // it posts, with those spaces as its memo — so showing it as service work is the honest
  // reading, even though the number then falls back to the "S.O." placeholder below.
  const fields: ServiceOrderRowFields = {
    serviceOrderText: entry?.serviceOrderText ?? hp?.serviceOrderText ?? null,
    serviceOrderID: entry?.serviceOrderID ?? hp?.serviceOrderID ?? null,
    serviceOrderAltID: entry?.serviceOrder?.altServiceOrderID ?? hp?.serviceOrderAltID ?? null,
    tempServiceOrderDescription: entry?.tempServiceOrderDescription ?? hp?.tempServiceOrderDescription ?? null,
  };
  const temp = (fields.tempServiceOrderDescription ?? '').trim();
  const linkedID = fields.serviceOrderID;
  const altID = (fields.serviceOrderAltID ?? '').trim();

  // The alt id arm is DISPLAY-ONLY and has no counterpart in the API's test, which looks
  // at the FK alone. A number can only reach us alongside its FK, so the two agree on every
  // real row; this keeps a read that hydrated only the number from rendering as "no service
  // order" if one ever appears.
  if (!isServiceOrderRow(fields) && altID === '') return null;

  const seq = entry?.serviceOrderScope?.scopeSeq ?? hp?.scopeSeq ?? null;
  const division = entry?.serviceOrderScope?.division ?? hp?.scopeDivision ?? null;
  const description = entry?.serviceOrderScope?.description ?? hp?.scopeDescription ?? null;
  // A JC job rides only on a backcharge (type J) service order; ordinary service work has
  // none, so it is appended rather than given a reserved slot.
  const job = entry?.serviceOrderScope?.job ?? hp?.scopeJob ?? null;

  // "Validated" is the ServiceOrder link, not the scope link. A resolved punch always has
  // both, but reading the SO id keeps a (theoretically) scope-less resolved punch out of
  // the "Not validated" bucket, where it would read as a data problem it is not.
  const validated = linkedID != null || altID !== '';

  return {
    // Exactly the memo the exporter will send, trimmed for display only.
    number: serviceOrderMemoText(fields).trim() || 'S.O.',
    name: entry?.serviceOrder?.name || null,
    scope: [
      seq != null ? `Scope ${seq}` : null,
      division,
      description,
      job ? `Job ${job}` : null,
    ].filter((v): v is string => v != null && v !== '').join(' · '),
    state: validated ? null : temp !== '' ? 'Awaiting validation' : 'Not validated',
    validated,
    serviceOrderScopeID: entry?.serviceOrderScope?.serviceOrderScopeID
      ?? entry?.serviceOrderScopeID
      ?? hp?.serviceOrderScopeID
      ?? null,
  };
}

/**
 * The compact one-line form used where there is no room for a block: Vista Export V2's
 * service-order row and the Post Verification report.
 *
 *   `SO 2912345 · Scope 2 · PLUMBING`      (validated)
 *   `SO 241234 · Awaiting validation`      (temporary)
 *   `SO 241234 · Not validated`            (legacy)
 */
export function formatServiceOrderLine(so: NpmServiceOrderInfo): string {
  return [`SO ${so.number}`, so.scope || null, so.state].filter((v) => v != null && v !== '').join(' · ');
}
